const normalizeQuestionTitle = (title) =>
  String(title || "")
    .trim()
    .toLowerCase()
    .replace(/\.$/, "");

export const normalizeDigitsOnlyInput = (value) =>
  String(value || "").replace(/[^0-9]/g, "");

export const normalizeProfitShareInput = normalizeDigitsOnlyInput;

export const normalizePercentInput = (value) => {
  const raw = String(value || "").replace(/[^0-9.]/g, "");
  const [whole, ...fractionParts] = raw.split(".");
  if (!fractionParts.length) return whole;
  return `${whole}.${fractionParts.join("")}`;
};

export const normalizeAmountInput = (value) =>
  String(value || "").replace(/[^0-9]/g, "");

export const formatAmountInput = (value) => {
  const normalized = normalizeAmountInput(value);
  if (!normalized) return "";
  return Number(normalized).toLocaleString("en-IN");
};

const parseNumericValue = (value) => {
  const normalized = String(value || "").replace(/[^0-9.-]/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const JV_PROJECT_VALUE_QUESTION_TITLES = [
  "Please specify the Project Value",
  "Please specify the project tenure in months",
  "Project Value (₹)",
];

const JV_FUND_REQUIRED_QUESTION_TITLES = [
  "Please specify the total amount of funding required for the project",
  "Fund Required (₹)",
];

const JV_PROJECT_DURATION_QUESTION_TITLES = [
  "What is the expected duration of the project?",
  "Project Duration (Months)",
];

const JV_EXPECTED_ROI_QUESTION_TITLES = [
  "What is the expected ROI (%) for the project?",
  "Expected ROI (%)",
];

const JV_PROFIT_SHARE_QUESTION_TITLES = [
  "What percentage of profit share are you offering to investors?",
  "Profit Share (%)",
];

const matchesQuestionTitle = (title, candidates) => {
  const normalized = normalizeQuestionTitle(title);
  return candidates.some((candidate) => normalizeQuestionTitle(candidate) === normalized);
};

export const isJvProjectValueQuestion = (title) =>
  matchesQuestionTitle(title, JV_PROJECT_VALUE_QUESTION_TITLES);

export const isJvFundRequiredQuestion = (title) =>
  matchesQuestionTitle(title, JV_FUND_REQUIRED_QUESTION_TITLES);

export const isJvProjectDurationQuestion = (title) =>
  matchesQuestionTitle(title, JV_PROJECT_DURATION_QUESTION_TITLES);

export const isJvExpectedRoiQuestion = (title) =>
  matchesQuestionTitle(title, JV_EXPECTED_ROI_QUESTION_TITLES);

export const isJvProfitShareQuestion = (title) =>
  matchesQuestionTitle(title, JV_PROFIT_SHARE_QUESTION_TITLES);

export const isJvAmountQuestion = (title) =>
  isJvProjectValueQuestion(title) || isJvFundRequiredQuestion(title);

export const isJvPercentQuestion = (title) =>
  isJvExpectedRoiQuestion(title) || isJvProfitShareQuestion(title);

const requiredRule = { required: true, message: "Please enter your response" };

const createMinAmountRule = (label, minAmount = 200000) => ({
  validator(_, value) {
    if (value === undefined || value === null || value === "") {
      return Promise.resolve();
    }

    const num = parseNumericValue(value);
    if (!Number.isFinite(num) || num < minAmount) {
      return Promise.reject(
        new Error(`${label} must be at least 2,00,000`),
      );
    }

    return Promise.resolve();
  },
});

const createMaxAmountRule = (label, maxAmount = 100000000000000) => ({
  validator(_, value) {
    if (value === undefined || value === null || value === "") {
      return Promise.resolve();
    }

    const num = parseNumericValue(value);
    if (Number.isFinite(num) && num > maxAmount) {
      return Promise.reject(
        new Error(`${label} cannot exceed ₹1,00,00,00,00,00,000`),
      );
    }

    return Promise.resolve();
  },
});

export const jvProjectValueAnswerRules = [
  requiredRule,
  createMinAmountRule("Project value"),
  createMaxAmountRule("Project value"),
];

export const jvFundRequiredAnswerRules = [
  requiredRule,
  createMinAmountRule("Fund required"),
  createMaxAmountRule("Fund required"),
];

export const jvProjectDurationAnswerRules = [
  requiredRule,
  {
    validator(_, value) {
      if (value === undefined || value === null || value === "") {
        return Promise.resolve();
      }

      const normalized = normalizeDigitsOnlyInput(value);
      if (!normalized) {
        return Promise.reject(new Error("Project duration should contain only numbers"));
      }

      const num = Number(normalized);
      if (num < 1) {
        return Promise.reject(new Error("Project duration must be at least 1 month"));
      }
      if (num > 200) {
        return Promise.reject(new Error("Project duration should not exceed 200 months"));
      }

      return Promise.resolve();
    },
  },
];

export const jvExpectedRoiAnswerRules = [
  requiredRule,
  {
    validator(_, value) {
      if (value === undefined || value === null || value === "") {
        return Promise.resolve();
      }

      const sanitized = normalizePercentInput(value);
      if (!sanitized) {
        return Promise.reject(new Error("ROI should contain only numbers"));
      }

      const num = Number(sanitized);
      if (!Number.isFinite(num) || num <= 0) {
        return Promise.reject(new Error("ROI must be greater than 0"));
      }
      if (num >= 100) {
        return Promise.reject(new Error("ROI should be less than 100%"));
      }

      return Promise.resolve();
    },
  },
];

export const jvProfitShareAnswerRules = [
  requiredRule,
  {
    validator(_, value) {
      if (value === undefined || value === null || value === "") {
        return Promise.resolve();
      }

      const normalized = normalizeDigitsOnlyInput(value);
      if (!normalized) {
        return Promise.reject(new Error("Profit share should contain only numbers"));
      }

      const num = Number(normalized);
      if (num < 0) {
        return Promise.reject(new Error("Profit share cannot be negative"));
      }
      if (num > 100) {
        return Promise.reject(new Error("Profit share should not exceed 100%"));
      }

      return Promise.resolve();
    },
  },
];

const JV_PROJECT_VALUE_DISPLAY = {
  questionTitle: "Please specify the Project Value",
  description: "Project value must be at least 2,00,000",
  placeholder: "Enter project value",
};

const JV_FIELD_KEY_BY_TITLE = {
  "project name": "projectName",
  "please provide the name of the project": "projectName",
  "project description": "projectDescription",
  "please provide a brief description of the project": "projectDescription",
  "project duration (months)": "projectDuration",
  "what is the expected duration of the project?": "projectDuration",
  "project value (₹)": "projectValue",
  "please specify the project value": "projectValue",
  "please specify the project tenure in months": "projectValue",
  "fund required (₹)": "fundRequired",
  "please specify the total amount of funding required for the project": "fundRequired",
  "expected roi (%)": "expectedRoi",
  "what is the expected roi (%) for the project?": "expectedRoi",
  "profit share (%)": "profitShare",
  "what percentage of profit share are you offering to investors?": "profitShare",
  "investment structure": "investmentStructure",
  "please provide a description of the proposed investment structure": "investmentStructure",
  "revenue / profit sharing model": "profitSharingModel",
  "please describe the proposed revenue/profit-sharing model": "profitSharingModel",
  "risk factors (optional)": "riskFactors",
  "please provide details of any potential risk factors associated with the project (optional)": "riskFactors",
  "reference links (optional)": "referenceLinks",
  "please provide any additional relevant links, along with a brief description of each link": "referenceLinks",
  "project proposal": "projectProposal",
  "please upload the relevant deal document": "projectProposal",
  "financial projections": "financialProjections",
  "please upload the financial projections for the project": "financialProjections",
  "supporting documents (optional)": "supportingDocs",
  "please upload any additional supporting documents, if applicable (optional)": "supportingDocs",
};

const QUESTION_STATUS_RANK = {
  APPROVED: 4,
  ANSWERED: 3,
  REJECTED: 2,
  PENDING: 1,
};

export const getJvQuestionDisplay = (question = {}) => {
  const normalizedTitle = normalizeQuestionTitle(question.questionTitle);
  const normalizedDescription = normalizeQuestionTitle(question.description);
  const isLegacyTitle =
    normalizedTitle === "please specify the project tenure in months" ||
    normalizedTitle === "project value (₹)" ||
    normalizedTitle === "project value ()";
  const isLegacyDescription =
    normalizedDescription === "project tenure in months" ||
    /^project value must be at least/.test(normalizedDescription);

  if (!isLegacyTitle && !isLegacyDescription) {
    return question;
  }

  return {
    ...question,
    questionTitle: isLegacyTitle
      ? JV_PROJECT_VALUE_DISPLAY.questionTitle
      : question.questionTitle,
    description: JV_PROJECT_VALUE_DISPLAY.description,
    placeholder: question.placeholder || JV_PROJECT_VALUE_DISPLAY.placeholder,
  };
};

export const getOrganizationQuestionDedupeKey = (question = {}) => {
  const display = getJvQuestionDisplay(question);
  const title = normalizeQuestionTitle(display.questionTitle);
  const fieldKey = JV_FIELD_KEY_BY_TITLE[title];
  const isManualKycThread =
    String(question.threadName || "")
      .trim()
      .toLowerCase() === "manual kyc";
  const prefix = isManualKycThread ? "manual-kyc:" : "";

  if (fieldKey) {
    return `${prefix}field:${fieldKey}`;
  }

  if (title) {
    return `${prefix}title:${title}`;
  }

  if (question.questionId) {
    return `${prefix}id:${question.questionId}`;
  }

  return `${prefix}thread-question:${question.threadQuestionId}`;
};

const getQuestionStatusRank = (question = {}) =>
  QUESTION_STATUS_RANK[question.status] || 0;

const getLastAnsweredTime = (question = {}) => {
  const fromQuestion = new Date(question.lastAnsweredAt || 0).getTime();
  if (fromQuestion) {
    return fromQuestion;
  }

  const answers = question.answers || [];
  if (!answers.length) {
    return 0;
  }

  return new Date(answers[0].submittedAt || 0).getTime();
};

export const dedupeOrganizationQuestions = (questions = []) => {
  const byKey = new Map();

  questions.forEach((question) => {
    const key = getOrganizationQuestionDedupeKey(question);
    const existing = byKey.get(key);

    if (!existing) {
      byKey.set(key, question);
      return;
    }

    const existingRank = getQuestionStatusRank(existing);
    const nextRank = getQuestionStatusRank(question);

    if (nextRank > existingRank) {
      byKey.set(key, question);
      return;
    }

    if (nextRank < existingRank) {
      return;
    }

    if (getLastAnsweredTime(question) >= getLastAnsweredTime(existing)) {
      byKey.set(key, question);
    }
  });

  return Array.from(byKey.values());
};

export const getQuestionnaireTextRules = (questionTitle) => {
  if (isJvProjectValueQuestion(questionTitle)) return jvProjectValueAnswerRules;
  if (isJvFundRequiredQuestion(questionTitle)) return jvFundRequiredAnswerRules;
  if (isJvProjectDurationQuestion(questionTitle)) return jvProjectDurationAnswerRules;
  if (isJvExpectedRoiQuestion(questionTitle)) return jvExpectedRoiAnswerRules;
  if (isJvProfitShareQuestion(questionTitle)) return jvProfitShareAnswerRules;
  return [requiredRule];
};

export const isReferenceLinksQuestion = (questionTitle) =>
  JV_FIELD_KEY_BY_TITLE[normalizeQuestionTitle(questionTitle)] ===
  "referenceLinks";

export const formatReferenceLinksAnswerForDisplay = (answerText) => {
  if (answerText == null || answerText === "") return "";
  const trimmed = String(answerText).trim();
  if (!trimmed || trimmed.toUpperCase() === "N/A") return trimmed;

  try {
    const parsed = JSON.parse(trimmed);
    if (!Array.isArray(parsed) || !parsed.length) return trimmed;
    return parsed
      .map((entry, index) => {
        const url = entry?.url || "";
        const description = entry?.description || "";
        if (!url && !description) return null;
        return `${index + 1}. ${url}${description ? ` — ${description}` : ""}`;
      })
      .filter(Boolean)
      .join("\n");
  } catch {
    return trimmed;
  }
};

export const formatQuestionnaireAnswerForDisplay = (questionTitle, value) => {
  if (value == null || value === "") return value;
  if (isJvAmountQuestion(questionTitle)) {
    return formatAmountInput(value);
  }
  if (isJvProjectDurationQuestion(questionTitle)) {
    return normalizeDigitsOnlyInput(value);
  }
  return value;
};

export const sanitizeQuestionnaireAnswerForSubmit = (questionTitle, value) => {
  if (value == null || value === "") return value;

  if (isJvAmountQuestion(questionTitle)) {
    return normalizeAmountInput(value);
  }

  if (isJvProjectDurationQuestion(questionTitle)) {
    return normalizeDigitsOnlyInput(value);
  }

  if (isJvExpectedRoiQuestion(questionTitle)) {
    const num = parseNumericValue(value);
    return Number.isFinite(num) ? String(num) : normalizePercentInput(value);
  }

  if (isJvProfitShareQuestion(questionTitle)) {
    return normalizeDigitsOnlyInput(value);
  }

  return value;
};

export const getQuestionnaireInputProps = (questionTitle) => {
  if (isJvAmountQuestion(questionTitle)) {
    return {
      normalize: normalizeAmountInput,
      getValueProps: (value) => ({ value: formatAmountInput(value) }),
      inputMode: "numeric",
    };
  }

  if (isJvProjectDurationQuestion(questionTitle)) {
    return {
      normalize: normalizeDigitsOnlyInput,
      inputMode: "numeric",
    };
  }

  if (isJvProfitShareQuestion(questionTitle)) {
    return {
      normalize: normalizeDigitsOnlyInput,
      inputMode: "numeric",
    };
  }

  if (isJvExpectedRoiQuestion(questionTitle)) {
    return {
      normalize: normalizePercentInput,
      inputMode: "decimal",
    };
  }

  return {};
};
